import {
    CloseOutlined,
    FilterAltOffOutlined,
    FilterAltOutlined,
} from "@mui/icons-material";
import {
    Alert,
    Button,
    Card,
    Chip,
    Grid,
    IconButton,
    TextField,
    Typography,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import { Box } from "@mui/system";
import { useQuery } from "@tanstack/react-query";
import { FormEvent, useEffect, useState } from "react";
import { GET } from "../../../Utilities/BaseService";
import { MultiPermissionAuthorize } from "../../../Utilities/MultiPermissionAuthorize";
import UpdatedSearchableInput from "../../../Utilities/UpdatedSearchableInput";
import { getSelectedFilters } from "../../Customer/Property/Property";
import SearchableInput from "../../Dashboard/Components/SearchableInput";
import BaseTable from "../../Table/BaseTable";
import { currencyFormatter } from "../Bill/Cells/Last12Summary";

type Filter = {
    district: number | "" | "all";
    village: number | "" | "all";
    hamlet: number | "" | "all";
    meterNo: string;
    powerstation: number | "";
    connectionType: number | "" | "all";
    selectedOrder: any;
    selectedColumn: any;
    billingMonthStart: string;
    billingMonthEnd: string;
};

const initialFilter: Filter = {
    district: "",
    village: "",
    hamlet: "",
    meterNo: "",
    powerstation: "",
    connectionType: "",
    selectedOrder: "",
    selectedColumn: "",
    billingMonthStart: "",
    billingMonthEnd: "",
};

const initialClear = {
    district: false,
    village: false,
    hamlet: false,
    powerstation: false,
    connectionType: false,
    billingMonthStart: false,
    billingMonthEnd: false,
};

export const getMonthWiseCollectionReport = (pagination: any, filter: any) => {
    return GET("/reading/collection/collections-report-month-wise", {
        meterNo: filter.meterNo ? filter.meterNo : undefined,
        district:
            filter.district && filter.district !== "all"
                ? filter.district
                : undefined,
        powerstation: filter.powerstation ? filter.powerstation : undefined,
        village:
            filter.village && filter.village !== "all"
                ? filter.village
                : undefined,
        hamlet:
            filter.hamlet && filter.hamlet !== "all"
                ? filter.hamlet
                : undefined,
        connectionType:
            filter.connectionType && filter.connectionType !== "all"
                ? filter.connectionType
                : undefined,
        billingMonthStart: filter.billingMonthStart
            ? filter.billingMonthStart
            : undefined,
        billingMonthEnd: filter.billingMonthEnd
            ? filter.billingMonthEnd
            : undefined,
        page: pagination.page + 1,
        limit: pagination.limit,
        selectedOrder: filter.selectedOrder ? filter.selectedOrder : undefined,
        selectedColumn: filter.selectedColumn
            ? filter.selectedColumn
            : undefined,
    });
};

const getMonthsInRange = (start: string, end: string) => {
    if (!start || !end) return [];
    const months = [];
    let [sYear, sMonth] = start.split("-").map(Number);
    const [eYear, eMonth] = end.split("-").map(Number);

    let currentYear = sYear;
    let currentMonth = sMonth;

    while (
        currentYear < eYear ||
        (currentYear === eYear && currentMonth <= eMonth)
    ) {
        const monthStr = String(currentMonth).padStart(2, "0");
        const monthKey = `${currentYear}-${monthStr}`;

        const date = new Date(currentYear, currentMonth - 1, 1);
        const monthName = date.toLocaleString("en-US", { month: "long" });

        months.push({
            key: monthKey,
            label: `${monthName} ${currentYear}`,
        });

        currentMonth++;
        if (currentMonth > 12) {
            currentMonth = 1;
            currentYear++;
        }
    }
    return months;
};

const MonthWiseCollectionReport = () => {
    const currentMonth = new Date().toISOString().slice(0, 7); // YYYY-MM

    const [pagination, setPagination] = useState<any>({
        page: 0,
        limit: 100,
        count: 0,
    });

    const [filter, setFilter] = useState<any>(initialFilter);
    const [intermediateFilter, setIntermediateFilter] = useState(initialFilter);
    const [feedback, setFeedback] = useState("");
    const [clearAll, setClearAll] = useState(initialClear);
    const [changeCount, setChangeCount] = useState(0);
    const [selectedFilters, setSelectedFilters] = useState({});
    const [isRequiredFiltersSelected, setIsRequiredFiltersSelected] =
        useState(false);

    const theme = useTheme();
    const mdDown = useMediaQuery(theme.breakpoints.down("md"));

    const { data: monthWiseData, isLoading } = useQuery(
        [
            "monthWiseCollectionReport",
            pagination.page,
            pagination.limit,
            {
                ...filter,
                meterNo: filter.meterNo ? filter.meterNo : undefined,
            },
            changeCount,
        ],
        () => getMonthWiseCollectionReport(pagination, filter),
        {
            enabled: Boolean(changeCount),
            onSuccess(res) {
                if (res.data) {
                    setPagination({
                        ...pagination,
                        count: res.data.count,
                    });

                    const firstRow = res.data.rows[0] ?? {};

                    const _selectedFills = getSelectedFilters(
                        {
                            district: firstRow.districtName
                                ? { name: firstRow.districtName }
                                : undefined,
                            village: firstRow.villageName
                                ? { name: firstRow.villageName }
                                : undefined,
                            hamlet: firstRow.hamletName
                                ? { name: firstRow.hamletName }
                                : undefined,
                            billingMonthStart: {
                                value: filter.billingMonthStart,
                            },
                            billingMonthEnd: { value: filter.billingMonthEnd },
                        },
                        [
                            "district",
                            "village",
                            "hamlet",
                            "billingMonthStart",
                            "billingMonthEnd",
                        ],
                        filter,
                        {
                            district: { label: "District", valueKey: "name" },
                            village: { label: "Village", valueKey: "name" },
                            hamlet: { label: "Hamlet", valueKey: "name" },
                            billingMonthStart: {
                                label: "Start month",
                                valueKey: "value",
                            },
                            billingMonthEnd: {
                                label: "End month",
                                valueKey: "value",
                            },
                        }
                    );

                    setSelectedFilters(() => _selectedFills);
                }
            },
        }
    );

    const handleApplyFilter = (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        const requiredFilters = {
            district: Boolean(
                intermediateFilter.district &&
                intermediateFilter.district !== "all"
            ),
            village: Boolean(
                intermediateFilter.village &&
                intermediateFilter.village !== "all"
            ),
            hamlet: Boolean(
                intermediateFilter.hamlet && intermediateFilter.hamlet !== "all"
            ),
            powerstation: intermediateFilter.powerstation,
            meterNo: intermediateFilter.meterNo,
            billingMonthStart: intermediateFilter.billingMonthStart,
            billingMonthEnd: intermediateFilter.billingMonthEnd,
        };

        if (
            (requiredFilters.district &&
                requiredFilters.powerstation &&
                requiredFilters.billingMonthStart &&
                requiredFilters.billingMonthEnd) ||
            (requiredFilters.meterNo &&
                requiredFilters.billingMonthStart &&
                requiredFilters.billingMonthEnd)
        ) {
            setIsRequiredFiltersSelected(true);
            setChangeCount((c) => c + 1);
        } else {
            setIsRequiredFiltersSelected(false);
            setChangeCount(0);
        }
    };

    useEffect(() => {
        if (changeCount) {
            setFilter({
                ...filter,
                ...intermediateFilter,
            });
        }
    }, [changeCount]);

    const dynamicMonths = getMonthsInRange(
        filter.billingMonthStart,
        filter.billingMonthEnd
    );

    const headers: Record<string, string> = {
        meterNo: "Meter No.",
        name: "Customer Name",
        fatherName: "Father Name",
        meterStatus: "Status",
        districtName: "District",
        villageName: "Village",
        hamletName: "Hamlet",
        connectionType: "Connection Type",
    };

    dynamicMonths.forEach((m) => {
        headers[`${m.key}_amount`] = `${m.label} Amount`;
        headers[`${m.key}_adjustment`] = `${m.label} Adjustment`;
    });

    const defaultSelectedHeaders = [
        "meterNo",
        "name",
        "meterStatus",
        ...dynamicMonths.flatMap((m) => [
            `${m.key}_amount`,
            `${m.key}_adjustment`,
        ]),
    ];

    const createSummary = (cols: string[]) => {
        const summaryRow = ["Total"];

        if (
            !monthWiseData ||
            !monthWiseData.data ||
            !monthWiseData.data.rows ||
            !Array.isArray(monthWiseData.data.rows)
        ) {
            return summaryRow;
        }

        const sums: Record<string, number> = {};
        cols.forEach((col) => {
            sums[col] = 0;
        });

        for (const row of monthWiseData.data.rows) {
            cols.forEach((col) => {
                if (row[col] !== undefined && typeof row[col] === "number") {
                    sums[col] += row[col];
                }
            });
        }

        for (const col of cols) {
            const isMonthCol = dynamicMonths.some((m) => m.key === col);
            if (isMonthCol) {
                summaryRow.push(currencyFormatter.format(sums[col]));
            } else {
                summaryRow.push("");
            }
        }

        return summaryRow;
    };

    const handleClearAll = () => {
        setIntermediateFilter(initialFilter);
        setFilter(initialFilter);
        setClearAll({
            district: true,
            village: true,
            powerstation: true,
            hamlet: true,
            connectionType: true,
            billingMonthStart: true,
            billingMonthEnd: true,
        });

        setChangeCount(0);
        setPagination({ page: 0, limit: 100, count: 0 });

        setTimeout(
            () =>
                setClearAll({
                    district: false,
                    village: false,
                    powerstation: false,
                    hamlet: false,
                    connectionType: false,
                    billingMonthStart: false,
                    billingMonthEnd: false,
                }),
            0
        );
    };

    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
            }}
        >
            <Card
                elevation={0}
                sx={{
                    bgcolor: theme.palette.common.white,
                    p: 2,
                    mb: "1rem",
                }}
            >
                <Grid
                    container
                    alignItems="center"
                    spacing={1.5}
                    justifyContent="space-between"
                >
                    <Grid item xs={12} md={4}>
                        <Typography variant="h5" color="GrayText">
                            Month-wise Collections Report
                        </Typography>
                    </Grid>
                </Grid>

                <Grid
                    item
                    container
                    spacing={1.5}
                    mt={0.5}
                    component="form"
                    onSubmit={handleApplyFilter}
                >
                    <MultiPermissionAuthorize ops={["READ DISTRICT"]}>
                        <Grid item xs={12} sm={6} lg={3}>
                            <SearchableInput
                                api="/region/district"
                                label="District"
                                multiple={false}
                                filter={intermediateFilter}
                                setFilter={setIntermediateFilter}
                                clearAll={clearAll.district}
                            />
                        </Grid>
                    </MultiPermissionAuthorize>

                    <MultiPermissionAuthorize ops={["READ POWERSTATION"]}>
                        <Grid item xs={12} sm={6} lg={3}>
                            <UpdatedSearchableInput
                                label="Powerstation"
                                api="/powerstation"
                                filter={intermediateFilter}
                                setFilter={setIntermediateFilter}
                                dep={intermediateFilter.district}
                                params={{
                                    district: intermediateFilter.district,
                                }}
                                clearAll={clearAll.powerstation}
                            />
                        </Grid>
                    </MultiPermissionAuthorize>

                    <MultiPermissionAuthorize ops={["READ VILLAGE"]}>
                        <Grid item xs={12} sm={6} lg={3}>
                            <UpdatedSearchableInput
                                api="/region/village"
                                label="Village"
                                multiple={false}
                                filter={intermediateFilter}
                                setFilter={setIntermediateFilter}
                                dep={[
                                    intermediateFilter.district,
                                    intermediateFilter.powerstation,
                                ]}
                                params={{
                                    district: intermediateFilter.district,
                                    powerstation:
                                        intermediateFilter.powerstation,
                                }}
                                clearAll={clearAll.village}
                            />
                        </Grid>
                    </MultiPermissionAuthorize>

                    <MultiPermissionAuthorize ops={["READ HAMLET"]}>
                        <Grid item xs={12} sm={6} lg={3}>
                            <UpdatedSearchableInput
                                api="/region/hamlet"
                                label="Hamlet"
                                multiple={false}
                                filter={intermediateFilter}
                                setFilter={setIntermediateFilter}
                                dep={[
                                    intermediateFilter.village,
                                    intermediateFilter.powerstation,
                                ]}
                                params={{
                                    village: intermediateFilter.village,
                                    powerstation:
                                        intermediateFilter.powerstation,
                                }}
                                clearAll={clearAll.hamlet}
                            />
                        </Grid>
                    </MultiPermissionAuthorize>

                    <MultiPermissionAuthorize ops={["READ CONNECTION_TYPE"]}>
                        <Grid item xs={12} sm={6} lg={3}>
                            <UpdatedSearchableInput
                                key={intermediateFilter.connectionType}
                                label="Connection Type"
                                api="/connection"
                                filter={intermediateFilter}
                                setFilter={setIntermediateFilter}
                                params={{
                                    district: intermediateFilter.district,
                                }}
                                multiple={true}
                                clearAll={clearAll.connectionType}
                            />
                        </Grid>
                    </MultiPermissionAuthorize>

                    <MultiPermissionAuthorize ops={["READ METER"]}>
                        <Grid item xs={12} sm={6} lg={3}>
                            <TextField
                                fullWidth
                                label="Meter No."
                                size="small"
                                type="text"
                                value={intermediateFilter.meterNo}
                                name="meterNo"
                                onChange={(e) =>
                                    setIntermediateFilter({
                                        ...intermediateFilter,
                                        meterNo: e.target.value,
                                    })
                                }
                            />
                        </Grid>
                    </MultiPermissionAuthorize>

                    <Grid item xs={12} sm={6} lg={3}>
                        <TextField
                            size="small"
                            name="billingMonthStart"
                            label="Billing Month Start"
                            type="month"
                            fullWidth
                            value={intermediateFilter.billingMonthStart}
                            onChange={(e) =>
                                setIntermediateFilter({
                                    ...intermediateFilter,
                                    billingMonthStart: e.target.value,
                                })
                            }
                            InputLabelProps={{ shrink: true }}
                            inputProps={{
                                max: currentMonth,
                                min: "2025-10",
                            }}
                            InputProps={{
                                endAdornment:
                                    intermediateFilter.billingMonthStart ? (
                                        <IconButton
                                            size="small"
                                            onClick={() =>
                                                setIntermediateFilter({
                                                    ...intermediateFilter,
                                                    billingMonthStart: "",
                                                })
                                            }
                                        >
                                            <CloseOutlined />
                                        </IconButton>
                                    ) : null,
                            }}
                        />
                    </Grid>

                    <Grid item xs={12} sm={6} lg={3}>
                        <TextField
                            size="small"
                            name="billingMonthEnd"
                            label="Billing Month End"
                            type="month"
                            fullWidth
                            value={intermediateFilter.billingMonthEnd}
                            onChange={(e) =>
                                setIntermediateFilter({
                                    ...intermediateFilter,
                                    billingMonthEnd: e.target.value,
                                })
                            }
                            InputLabelProps={{ shrink: true }}
                            inputProps={{
                                max: currentMonth,
                                min:
                                    intermediateFilter.billingMonthStart ||
                                    "2025-10",
                            }}
                            InputProps={{
                                endAdornment:
                                    intermediateFilter.billingMonthEnd ? (
                                        <IconButton
                                            size="small"
                                            onClick={() =>
                                                setIntermediateFilter({
                                                    ...intermediateFilter,
                                                    billingMonthEnd: "",
                                                })
                                            }
                                        >
                                            <CloseOutlined />
                                        </IconButton>
                                    ) : null,
                            }}
                        />
                    </Grid>

                    <Grid item xs={12} sm={6} lg={3}>
                        <Button
                            color="secondary"
                            variant="outlined"
                            type="submit"
                            sx={{
                                height: "100%",
                            }}
                            startIcon={<FilterAltOutlined />}
                            fullWidth
                        >
                            Search
                        </Button>
                    </Grid>

                    <Grid item xs={12} sm={6} lg={3}>
                        <Button
                            color="error"
                            variant="outlined"
                            startIcon={<FilterAltOffOutlined />}
                            onClick={handleClearAll}
                            sx={{
                                height: "100%",
                                minWidth: theme.spacing(20),
                            }}
                            fullWidth
                        >
                            Clear All
                        </Button>
                    </Grid>
                </Grid>
            </Card>

            <Box sx={{ height: "auto", paddingTop: "1rem" }}>
                {!isRequiredFiltersSelected && (
                    <Alert severity="info">
                        Please select "District", "PowerStation", "Billing Month
                        Start" and "Billing Month End" or "Meter Number" with
                        "Billing Month Start" and "Billing Month End"
                    </Alert>
                )}

                {!isLoading &&
                    monthWiseData?.data?.rows?.length === 0 &&
                    isRequiredFiltersSelected && (
                        <Alert severity="info">No data found.</Alert>
                    )}

                {isRequiredFiltersSelected && (
                    <BaseTable
                        load={Boolean(changeCount)}
                        headers={headers}
                        defaultSelectedHeaders={defaultSelectedHeaders}
                        data={monthWiseData?.data?.rows?.map((row: any) => {
                            const newRow = { ...row };

                            // Render status as a colored chip
                            const statusColor: Record<
                                string,
                                "success" | "default" | "warning"
                            > = {
                                active: "success",
                                inactive: "default",
                                replaced: "warning",
                            };
                            newRow.meterStatus = (
                                <Chip
                                    label={
                                        row.meterStatus
                                            ? row.meterStatus
                                                  .charAt(0)
                                                  .toUpperCase() +
                                              row.meterStatus.slice(1)
                                            : "—"
                                    }
                                    color={
                                        statusColor[row.meterStatus] ??
                                        "default"
                                    }
                                    size="small"
                                    variant="outlined"
                                />
                            );

                            dynamicMonths.forEach((m) => {
                                const amountKey = `${m.key}_amount`;
                                const adjustmentKey = `${m.key}_adjustment`;

                                if (row[amountKey] !== undefined) {
                                    newRow[amountKey] =
                                        typeof row[amountKey] === "number"
                                            ? currencyFormatter.format(
                                                  row[amountKey]
                                              )
                                            : row[amountKey];
                                } else {
                                    newRow[amountKey] = "-";
                                }

                                if (row[adjustmentKey] !== undefined) {
                                    newRow[adjustmentKey] =
                                        typeof row[adjustmentKey] === "number"
                                            ? currencyFormatter.format(
                                                  row[adjustmentKey]
                                              )
                                            : row[adjustmentKey];
                                } else {
                                    newRow[adjustmentKey] = "-";
                                }
                            });
                            return newRow;
                        })}
                        delEndPoint=""
                        feedback={feedback}
                        setFeedback={setFeedback}
                        rowsCount={pagination.count}
                        pagination={pagination}
                        setPagination={setPagination}
                        isLoading={isLoading}
                        permissions={{
                            edit: [""],
                            delete: [""],
                        }}
                        queryKey={[
                            "monthWiseCollectionReport",
                            pagination.page,
                            pagination.limit,
                            {
                                ...filter,
                                meterNo: filter.meterNo
                                    ? filter.meterNo
                                    : undefined,
                            },
                            changeCount,
                        ]}
                        reportName="MonthWiseCollections__List"
                        reportDetails={{
                            district: filter.district
                                ? filter.district
                                : undefined,
                            powerstation: filter.powerstation
                                ? filter.powerstation
                                : undefined,
                            village: filter.village
                                ? filter.village
                                : undefined,
                            hamlet: filter.hamlet ? filter.hamlet : undefined,
                        }}
                        summaryRow={createSummary}
                    />
                )}
            </Box>
        </Box>
    );
};

export default MonthWiseCollectionReport;
